import React, { useEffect, useRef, useState } from "react";
import { useGuard } from "@/lib/guards";
import { liteVideoProfile } from "@/lib/liteProfile";

// Lot 677 — fil « fenêtré » : une publication à plus de 2 écrans de la zone visible est DÉMONTÉE (images, lecteurs
// vidéo, arbre React libérés) et remplacée par un bloc vide de la même hauteur (mesurée tant qu'elle est montée) →
// le défilement ne bouge pas, et la mémoire de la page ne grossit plus avec tout ce qui a défilé (cause des morts du
// moteur WebView : rechargement iPhone, fermeture de l'app Android). Elle se remonte dès qu'on s'en rapproche.
// Garde-fou admin « feed_window » (Paramètres › Identité) pour revenir à l'ancien comportement à distance.
export const FEED_WINDOW_MARGIN = "200% 0px 200% 0px";
export const FEED_WINDOW_MARGIN_LITE = "100% 0px 100% 0px";  // lot 734 — faible mémoire : 1 écran de marge, 3 publications montées au départ

export const FeedWindow = ({ children, index = 0, minHeight = 260, margin: marginProp, testId }) => {
  const enabled = useGuard("feed_window");
  const [lite] = useState(() => liteVideoProfile());
  const margin = marginProp || (lite ? FEED_WINDOW_MARGIN_LITE : FEED_WINDOW_MARGIN);
  const ref = useRef(null);
  const heightRef = useRef(0);
  const [near, setNear] = useState(index < (lite ? 3 : 6)); // les 6 premières montent tout de suite (lot 682 : 4 → 6, moins de « pop » au 1er défilement) ; 3 en profil allégé

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || typeof IntersectionObserver === "undefined") { setNear(true); return undefined; }
    const io = new IntersectionObserver(([e]) => {
      const on = !!e?.isIntersecting;
      if (!on) { const h = el.offsetHeight; if (h > 0) heightRef.current = h; } // dernière mesure juste avant le démontage
      setNear(on);
    }, { rootMargin: margin, threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled, margin]);

  useEffect(() => {
    const el = ref.current;
    if (!near || !el) return undefined;
    const measure = () => { const h = el.offsetHeight; if (h > 0) heightRef.current = h; };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [near]);

  const mounted = near || !enabled;
  return (
    <div ref={ref} style={mounted ? undefined : { height: heightRef.current || minHeight }} data-testid={testId} data-windowed={mounted ? "0" : "1"}>
      {mounted ? children : (
        // Lot 682 — silhouette de carte à la place d'un vide : sur un téléphone lent, la carte qui se monte en arrivant
        // à l'écran ne « flashe » plus ; même hauteur, aucune image, coût nul.
        <div className="yalola-card h-full p-4 feed-window-skeleton" aria-hidden="true" data-testid={testId ? `${testId}-skeleton` : undefined}>
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-full shrink-0" style={{ background: "var(--yalola-surface-alt)" }} />
            <span className="flex-1 space-y-2">
              <span className="block h-3 w-1/3 rounded-full" style={{ background: "var(--yalola-surface-alt)" }} />
              <span className="block h-2.5 w-1/5 rounded-full" style={{ background: "var(--yalola-surface-alt)" }} />
            </span>
          </div>
          <span className="block mt-4 h-3 w-5/6 rounded-full" style={{ background: "var(--yalola-surface-alt)" }} />
        </div>
      )}
    </div>
  );
};
